import React from 'react';
import { Solicitud } from '@/types/solicitud';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';

interface SolicitudTableProps {
  solicitudes: Solicitud[];
  onEdit?: (solicitud: Solicitud) => void;
  onView?: (solicitud: Solicitud) => void;
  onRowClick?: (solicitud: Solicitud) => void;
  renderActions?: (solicitud: Solicitud) => React.ReactNode;
  /** Monto a mostrar en la columna: 'solicitado' (default) o 'autorizado' */
  montoColumn?: 'solicitado' | 'autorizado';
  /** Clase CSS extra por fila (override del color por defecto) */
  rowClassName?: (solicitud: Solicitud) => string | undefined;
}

const EditIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
  </svg>
);

const ViewIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const formatCurrency = (amount: number | undefined) => {
  if (amount === undefined) return '$0.00';
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount);
};

const formatDate = (dateStr: string | undefined | null) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return '-';
  }
};

const getStatusVariant = (status: string) => {
  switch (status) {
    case 'FONDEADO': return 'success';
    case 'RECHAZADO': return 'danger';
    case 'AUTORIZADO': return 'info';
    case 'CONTACTO': return 'warning';
    default: return 'default';
  }
};

// Colores condicionales para las filas (como en legacy)
const getRowBgColor = (solicitud: Solicitud) => {
  const status = (solicitud.solicitudEstatus || '').toUpperCase();

  // Colores según estatus principal (Más vivos y claros)
  if (status === 'FONDEADO') return 'bg-green-100/60 border-l-4 border-green-500';
  if (status.includes('RECHAZADO') || status.includes('RECHAZO')) return 'bg-red-50 border-l-4 border-red-500';
  if (status.includes('CANCELADO')) return 'bg-gray-100/80 border-l-4 border-gray-400';

  if (status.includes('AUTORIZADO')) return 'bg-blue-50 border-l-4 border-blue-500';
  if (status === 'VOBO' || status.includes('VOBO')) return 'bg-purple-50 border-l-4 border-purple-500';
  if (status === 'PROPUESTA') return 'bg-yellow-50 border-l-4 border-yellow-400';

  // Default clean white (incluye ENTREGA A RIESGOS y el resto)
  return 'bg-white hover:bg-gray-50';
};

const getRowTextColor = (solicitud: Solicitud) => {
  const status = solicitud.solicitudEstatus;

  if (status === 'RECHAZADO' || status === 'RECHAZO RIESGOS' || status === 'RECHAZO DG') return 'text-red-700';
  if (status === 'FONDEADO') return 'text-green-700';

  return 'text-gray-900';
};

export const SolicitudTable: React.FC<SolicitudTableProps> = ({ solicitudes, onEdit, onView, onRowClick, renderActions, montoColumn = 'solicitado', rowClassName }) => {
  const handleRowClick = (solicitud: Solicitud) => {
    if (onRowClick) onRowClick(solicitud);
  };

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
      <table className="min-w-full divide-y divide-gray-200 bg-white">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-8">#</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Control</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px]">Cliente / Empresa</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Producto</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monto</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estatus</th>
            <th className="hidden md:table-cell px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Asesor</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {solicitudes.map((solicitud, index) => (
            <tr
              key={solicitud.id}
              role={onRowClick ? 'button' : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              onClick={onRowClick ? () => handleRowClick(solicitud) : undefined}
              onKeyDown={onRowClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') handleRowClick(solicitud); } : undefined}
              className={`${rowClassName?.(solicitud) || getRowBgColor(solicitud)} hover:opacity-90 transition-all duration-150 ${rowClassName?.(solicitud) ? '' : getRowTextColor(solicitud)} ${onRowClick ? 'cursor-pointer' : ''}`}
            >
              <td className="px-2 py-3 whitespace-nowrap text-center">
                <span className="text-xs font-bold text-gray-600">{index + 1}</span>
              </td>
              <td className="px-3 py-3 whitespace-nowrap">
                <span className="text-xs font-mono text-gray-500">#{solicitud.id}</span>
                <div className="text-xs font-bold text-gray-700">{solicitud.solicitudNumeroControl || 'S/N'}</div>
              </td>
              <td className="px-3 py-3 whitespace-nowrap">
                <span className="text-xs text-gray-600">{formatDate(solicitud.fechaContacto)}</span>
              </td>
              <td className="px-3 py-3">
                <div className="text-sm font-medium text-gray-900 leading-tight">
                  {([solicitud.clienteNombre, solicitud.clienteApellidoPaterno, solicitud.clienteApellidoMaterno].filter(Boolean).join(' ') || '-').toUpperCase()}
                </div>
                <div className="text-xs text-gray-500 leading-tight">{solicitud.clienteEmpresa || 'Sin Empresa'}</div>
              </td>
              <td className="px-3 py-3 whitespace-nowrap">
                <span className="text-xs text-gray-700">{solicitud.producto}</span>
              </td>
              <td className="px-3 py-3 whitespace-nowrap">
                <div className="text-xs font-semibold text-gray-900">
                  {formatCurrency(montoColumn === 'autorizado' ? solicitud.montoAutorizado : solicitud.montoSolicitado)}
                </div>
                <div className="text-[10px] text-gray-500">{montoColumn === 'autorizado' ? 'Autorizado' : 'Solicitado'}</div>
              </td>
              <td className="px-3 py-3 whitespace-nowrap">
                <Badge variant={getStatusVariant(solicitud.solicitudEstatus)}>
                  {solicitud.solicitudEstatus}
                </Badge>
              </td>
              <td className="hidden md:table-cell px-3 py-3 whitespace-nowrap text-xs text-gray-500">
                {solicitud.asesorNombre || '-'}
              </td>
              <td className="px-3 py-3 whitespace-nowrap text-left text-sm font-medium">
                <div className="flex items-center gap-2 flex-wrap">
                  {onView && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); onView(solicitud); }}
                      title="Ver detalle (solo lectura)"
                    >
                      <ViewIcon />
                    </Button>
                  )}
                  {onEdit && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); onEdit(solicitud); }}
                      title="Editar"
                    >
                      <EditIcon />
                    </Button>
                  )}
                  {renderActions && renderActions(solicitud)}
                  {!onEdit && !onView && !renderActions && <span className="text-gray-400">—</span>}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
